import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  MapPin, Calendar, Heart, Sparkles, Check, ChevronDown, ChevronUp,
  Star, Sprout, ArrowRight, Moon, Coffee, X, ChevronLeft, Wind
} from 'lucide-react';

/* ==================== ASSETS ==================== */
const HERO_BG = 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897832/2021_0412_04195800-02_qlibys.jpg';
const HERMI_PHOTO = 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1776874282/yo_landing_inicial_fmuidq.png';

const GALLERY_IMAGES = [
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779901081/2025_0813_00482500_vudcuv.jpg', caption: 'Vísperas en El Olivar' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897831/IMG_20210727_184947-01_ri4c5l.jpg', caption: 'Atardecer en el claustro' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779901081/2025_0813_00451800_nkunmt.jpg', caption: 'Edición anterior · agosto 2025' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897832/2019_0512_05011600-01_ycl97u.jpg', caption: 'La naturaleza alrededor' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779901080/WhatsApp_Image_2026-05-27_at_18.55.30_1_b8nqgj.jpg', caption: 'Círculo del grupo' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897831/2025_0821_22420600_g7gzq0.jpg', caption: 'Cielo estrellado de Estercuel' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779898181/0000_0000_00000000_91_gq94xt.jpg', caption: 'Rincones del monasterio' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779898181/OI000978_g6ihz3.jpg', caption: 'Vida monástica' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779901984/WhatsApp_Image_2026-05-27_at_19.12.17_1_wojqjp.jpg', caption: 'Encuentros que abrigan' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779901984/WhatsApp_Image_2026-05-27_at_19.12.18_2_ilw7r2.jpg', caption: 'Geometrías del círculo' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779901983/WhatsApp_Image_2026-05-27_at_19.12.18_3_hus267.jpg', caption: 'Presencia compartida' },
  { src: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779901983/WhatsApp_Image_2026-05-27_at_19.12.18_4_jphvt7.jpg', caption: 'Sostén consciente' },
];

/* ==================== HELPERS ==================== */
const formatDateRange = (start, end) => {
  if (!start || !end) return '';
  const s = new Date(start);
  const e = new Date(end);
  const months = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  if (s.getMonth() === e.getMonth()) {
    return `${s.getDate()} – ${e.getDate()} de ${months[s.getMonth()]} de ${s.getFullYear()}`;
  }
  return `${s.getDate()} ${months[s.getMonth()]} – ${e.getDate()} ${months[e.getMonth()]} ${s.getFullYear()}`;
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const target = new Date(iso);
  return Math.ceil((target - new Date()) / (1000 * 60 * 60 * 24));
};

/* ==================== SIGNUP MODAL ==================== */
const SignupModal = ({ retreat, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [intention, setIntention] = useState('');
  const [roomPref, setRoomPref] = useState('share');
  const [submitting, setSubmitting] = useState(false);
  const [specialPricing, setSpecialPricing] = useState(null);

  // Check if the logged-in user is eligible for the "Tarifa alumnado actual"
  useEffect(() => {
    if (!user || !retreat?.slug) { setSpecialPricing(null); return; }
    api.get(`/retreats/${retreat.slug}/special-pricing/me`)
      .then(r => { if (r.data?.eligible) setSpecialPricing(r.data); })
      .catch(() => {});
  }, [user, retreat?.slug]);

  const fmtPrice = (n) => Number(n).toFixed(2).replace('.', ',');
  const doubleRegular = fmtPrice(retreat?.pricing?.double?.amount ?? 452.5);
  const doubleSpecial = specialPricing ? fmtPrice(specialPricing.price_double) : null;

  const handleSubmit = async () => {
    if (intention.trim().length < 5) {
      toast.error('Cuéntanos brevemente qué te trae al retiro');
      return;
    }
    if (!user) {
      // Persist the signup intent so we can auto-complete it after login/register.
      sessionStorage.setItem('retreat_pending_signup', JSON.stringify({
        slug: retreat.slug, intention, roomPref,
      }));
      navigate(`/register?redirect=${encodeURIComponent(`/retiros/${retreat.slug}`)}`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/retreats/${retreat.slug}/signup`, {
        intention_text: intention,
        room_preference: roomPref,
      });
      toast.success('Tu plaza está reservada. Te llevamos a Mi Camino…');
      onSuccess && onSuccess();
      onClose();
      // Redirect to Mi Camino so the user can see they're enrolled, with focus on the retreat card.
      navigate(`/mi-camino?retreat=${retreat.slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al inscribirse');
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = () => {
    if (intention.trim().length >= 5) {
      sessionStorage.setItem('retreat_pending_signup', JSON.stringify({
        slug: retreat.slug, intention, roomPref,
      }));
    }
    navigate(`/login?redirect=${encodeURIComponent(`/retiros/${retreat.slug}`)}`);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
         data-testid="retreat-signup-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 z-10" data-testid="signup-close-btn">
          <X className="w-4 h-4 text-gray-500" />
        </button>
        <div className="p-7 sm:p-9">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider text-[#8a7428]">
            <Sparkles className="w-3.5 h-3.5" /> Reserva en lista · gratuita
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[hsl(var(--text-primary))] mb-3">
            Volver a ti, en El Olivar.
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            Antes de guardarte la plaza, cuéntame brevemente qué te trae aquí. No hace falta tenerlo claro: una palabra, una sensación, lo que asome.
          </p>

          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-2">
            ¿Qué vienes a soltar o a sembrar?
          </label>
          <textarea
            value={intention}
            onChange={(e) => setIntention(e.target.value)}
            placeholder="Vengo a descansar de verdad… / Vengo a soltar la hiperexigencia… / Necesito escucharme…"
            rows={4}
            className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none mb-5 resize-none"
            data-testid="signup-intention-input"
            maxLength={2000}
          />

          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-2">
            Tipo de habitación que te llama
          </label>
          <div className="grid grid-cols-2 gap-2 mb-2">
            {[
              { v: 'share', regular: doubleRegular, special: doubleSpecial, label: 'Doble compartida', sub: null },
              { v: 'single', regular: null, special: null, label: 'Individual', sub: 'Consultar disponibilidad' },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setRoomPref(opt.v)}
                className={`px-3 py-2 rounded-xl text-sm border transition-colors text-left ${
                  roomPref === opt.v
                    ? 'bg-[#D4A84B]/20 border-[#D4A84B] text-[#5e4f1a] font-medium'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-[#D4A84B]/40'
                }`}
                data-testid={`signup-room-${opt.v}`}
              >
                <span className="block">{opt.label}</span>
                {opt.special ? (
                  <span className="block text-xs mt-0.5">
                    <span className="line-through text-gray-400 mr-1.5">{opt.regular}€</span>
                    <strong className="text-[#8a7428]">{opt.special}€</strong>
                  </span>
                ) : opt.regular ? (
                  <span className="block text-xs text-gray-500 mt-0.5">{opt.regular}€</span>
                ) : (
                  <span className="block text-[11px] text-gray-400 italic mt-0.5">{opt.sub}</span>
                )}
              </button>
            ))}
          </div>
          {specialPricing && roomPref === 'share' && (
            <div className="mb-3 px-3 py-2 rounded-xl bg-gradient-to-r from-[#FDFBF5] to-white border border-[#D4A84B]/40 text-[11px] text-[#5e4f1a] leading-relaxed flex items-start gap-2"
                 data-testid="signup-special-pricing-note">
              <Sparkles className="w-3 h-3 text-[#D4A84B] mt-0.5 flex-shrink-0" />
              <span>
                <strong>{specialPricing.title || 'Precio alumnado actual'}.</strong>{' '}
                Como gesto de gratitud por estar en la formación, te corresponde un precio especial.
              </span>
            </div>
          )}
          {roomPref === 'single' && (
            <p className="text-[11px] text-amber-700 mb-6 italic">
              Solo hay 3 habitaciones individuales. Tu solicitud se revisará y te confirmaremos por email.
              Mientras tanto puedes pagar la señal al precio de habitación doble.
            </p>
          )}
          {roomPref !== 'single' && <div className="mb-6" />}

          <div className="bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-xl p-4 mb-6 text-xs text-gray-600 leading-relaxed">
            <strong className="text-[#8a7428]">Importante.</strong> Esta reserva guarda tu plaza en lista de forma <strong>gratuita</strong>.
            La plaza solo queda confirmada al recibir la <strong>paga y señal de 100€</strong>. Te enviaremos las instrucciones por email.
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#2d3748] hover:bg-black text-white py-3.5 rounded-full text-sm font-medium tracking-wide transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="signup-submit-btn"
          >
            {submitting ? 'Reservando…' : (
              <>Reservar mi plaza gratuitamente <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
          {!user && (
            <>
              <div className="relative my-3 text-center">
                <span className="inline-block px-3 bg-white text-[10px] uppercase tracking-wider text-gray-400 relative z-10">o</span>
                <span className="absolute top-1/2 left-0 right-0 h-px bg-gray-100 -z-0" />
              </div>
              <button
                onClick={goToLogin}
                type="button"
                className="w-full bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 py-3 rounded-full text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
                data-testid="signup-go-to-login-btn"
              >
                Ya tengo cuenta · acceder
              </button>
              <p className="text-xs text-gray-500 text-center mt-3 leading-relaxed">
                Si aún no tienes cuenta en la escuela, te la crearemos automáticamente.
                Una vez dentro, completaremos tu inscripción al retiro.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ==================== MAIN LANDING ==================== */
const RetreatLanding = () => {
  const { slug: routeSlug } = useParams();
  const slug = routeSlug || 'reinicia-2026';
  const { user } = useAuth();
  const [retreat, setRetreat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignup, setShowSignup] = useState(false);
  const [myEnrollment, setMyEnrollment] = useState(null);
  const [enrollmentChecked, setEnrollmentChecked] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Close lightbox with ESC + arrow keys for nav
  useEffect(() => {
    if (lightboxIdx == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      else if (e.key === 'ArrowRight') setLightboxIdx(i => (i + 1) % GALLERY_IMAGES.length);
      else if (e.key === 'ArrowLeft') setLightboxIdx(i => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx]);

  useEffect(() => {
    api.get(`/retreats/${slug}`)
      .then(r => setRetreat(r.data))
      .catch(() => toast.error('No pudimos cargar el retiro'))
      .finally(() => setLoading(false));
  }, [slug]);

  // If the URL has ?signup=1, auto-open the signup modal once data is loaded
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === '1' && retreat && !myEnrollment) {
      setShowSignup(true);
    }
  }, [retreat, myEnrollment]);

  useEffect(() => {
    if (!user) { setMyEnrollment(null); setEnrollmentChecked(false); return; }
    setEnrollmentChecked(false);
    api.get(`/retreats/${slug}/me`)
      .then(r => setMyEnrollment(r.data?.enrollment || null))
      .catch(() => setMyEnrollment(null))
      .finally(() => setEnrollmentChecked(true));
  }, [slug, user]);

  // Auto-complete signup if the user just logged in / registered and there's a pending signup.
  // This is the "tras darse de alta o acceder con su usuario, directamente les lleve a Mi Camino" flow.
  const navigate = useNavigate();
  useEffect(() => {
    if (!user || !retreat || !enrollmentChecked) return;
    const raw = sessionStorage.getItem('retreat_pending_signup');
    if (!raw) return;
    let pending;
    try { pending = JSON.parse(raw); } catch { sessionStorage.removeItem('retreat_pending_signup'); return; }
    if (!pending || pending.slug !== slug) return;
    // Clear immediately to avoid loops
    sessionStorage.removeItem('retreat_pending_signup');

    // Already enrolled → just take them to Mi Camino
    if (myEnrollment) {
      toast.success('Ya tienes plaza. Te llevamos a Mi Camino.');
      navigate(`/mi-camino?retreat=${slug}`);
      return;
    }
    // Not enrolled yet → POST the pending signup with the intention they wrote pre-login.
    api.post(`/retreats/${slug}/signup`, {
      intention_text: pending.intention || 'Reserva tras autenticación.',
      room_preference: pending.roomPref || 'share',
    })
      .then(() => {
        toast.success('Tu plaza está reservada. Te llevamos a Mi Camino…');
        navigate(`/mi-camino?retreat=${slug}`);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'No pudimos completar tu inscripción automáticamente. Pulsa "Reservar mi plaza" para reintentar.');
        setShowSignup(true);
      });
  }, [user, retreat, enrollmentChecked, myEnrollment, slug, navigate]);

  const dateRange = useMemo(() => retreat ? formatDateRange(retreat.date_start, retreat.date_end) : '', [retreat]);
  const days = useMemo(() => retreat ? daysUntil(retreat.date_start) : null, [retreat]);
  const earlyBirdActive = useMemo(() => {
    if (!retreat?.pricing?.early_bird_until) return false;
    return new Date() <= new Date(retreat.pricing.early_bird_until);
  }, [retreat]);

  const navItems = [
    { id: 'hero', label: 'Inicio' },
    { id: 'para-ti', label: 'Para ti si…' },
    { id: 'reiniciar', label: 'Reiniciar' },
    { id: 'cuando-vuelvas', label: 'Cuando vuelvas' },
    { id: 'experiencia', label: 'La experiencia' },
    { id: 'cartas', label: 'Cartas' },
    { id: 'lugar', label: 'El lugar' },
    { id: 'galeria', label: 'Galería' },
    { id: 'quien-soy', label: 'Quién soy' },
    { id: 'transporte', label: 'Transporte' },
    { id: 'inversion', label: 'Inversión' },
    { id: 'faq', label: 'FAQ' },
  ];

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    // Both nav bars: main app header (80px) + secondary tab nav (56px) = 136px
    const offset = 140;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#D4A84B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!retreat) {
    return <div className="min-h-screen pt-32 flex items-center justify-center bg-white text-gray-500">Retiro no disponible.</div>;
  }

  const ctaIsEnrolled = !!myEnrollment;
  const handlePrimaryCta = () => {
    if (ctaIsEnrolled) return; // they have the "go to my space" link rendered separately
    setShowSignup(true);
  };

  return (
    <div className="pt-20 bg-white" data-testid="retreat-landing">
      {/* SECONDARY NAV — like Facilitadoras/Límites */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[hsl(var(--border))] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 gap-3">
            <Link to="/viajes" className="flex items-center gap-1 text-sm text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] transition-colors flex-shrink-0">
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Viajes</span>
            </Link>
            <nav className="flex items-center gap-1 overflow-x-auto hide-scrollbar" data-testid="retreat-tabs">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors rounded-full whitespace-nowrap text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] hover:bg-[#FDFBF5]"
                  data-testid={`tab-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            {ctaIsEnrolled ? (
              <Link to={`/mi-retiro/${slug}`} className="hidden sm:inline-flex items-center gap-2 bg-[#1a3a5c] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#13294b] transition-colors flex-shrink-0">
                Mi espacio
              </Link>
            ) : (
              <button
                onClick={() => scrollToSection('inversion')}
                className="hidden sm:inline-flex items-center gap-2 bg-[#1a3a5c] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#13294b] transition-colors flex-shrink-0"
              >
                Reservar plaza
              </button>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE STICKY CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white border-t border-[hsl(var(--border))] p-3 shadow-2xl">
        <button
          onClick={() => ctaIsEnrolled ? null : scrollToSection('inversion')}
          className="w-full bg-[#1a3a5c] text-white py-4 rounded-full text-base font-medium hover:bg-[#13294b] transition-all flex items-center justify-center gap-2"
        >
          {ctaIsEnrolled ? (
            <Link to={`/mi-retiro/${slug}`} className="inline-flex items-center gap-2">Mi espacio del retiro <ArrowRight className="w-4 h-4" /></Link>
          ) : (<>Reservar plaza <ArrowRight className="w-4 h-4" /></>)}
        </button>
      </div>

      {/* HERO — Límites-style: background image with left→right blue-iniciatic gradient, text block left-aligned */}
      <section id="hero" className="relative min-h-[90vh] flex items-center mt-14 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} aria-hidden="true">
          {/* Iniciatic blue gradient: heavy on the left (where the text sits), revealing image on the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b1830]/95 via-[#0e2144]/85 to-[#1a3a5c]/35" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-24 w-full">
          <div className="max-w-3xl">
            {/* Badges */}
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 animate-fade-in">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-xs sm:text-sm font-medium">
                <Star className="w-3.5 h-3.5" />
                Retiro presencial
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-xs sm:text-sm font-medium">
                <Calendar className="w-3.5 h-3.5" />
                {dateRange}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D4A84B]/20 backdrop-blur-sm text-[#e8c75a] rounded-full text-xs sm:text-sm font-medium">
                <MapPin className="w-3.5 h-3.5" />
                {retreat.location_name}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading text-white tracking-tight mb-3 sm:mb-4 animate-slide-up leading-[1.05]">
              REINICIA 2026
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl font-heading text-[#e8c75a] mb-8 sm:mb-10 animate-slide-up stagger-1 italic">
              4 días para reiniciar tu vida desde dentro.
            </p>

            {/* Description */}
            <div className="text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl mb-10 sm:mb-12 animate-slide-up stagger-2 space-y-4">
              <p>
                Hay momentos en los que no necesitas seguir esforzándote más.
                Necesitas detenerte, ordenar tu energía, soltar lo que ya no sostiene tu vida actual y volver a escucharte desde un lugar más limpio, presente y verdadero.
              </p>
              <p className="text-white/95">
                REINICIA es un retiro presencial para cerrar una etapa, recuperar tu eje interno y volver a tu vida con más claridad.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-3 sm:gap-4 animate-slide-up stagger-3">
              {ctaIsEnrolled ? (
                <Link
                  to={`/mi-retiro/${slug}`}
                  className="w-full sm:w-auto bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-4 rounded-full text-base font-medium transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 shadow-lg"
                  data-testid="go-to-private-panel"
                >
                  Entrar a mi espacio del retiro
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <button
                  onClick={() => setShowSignup(true)}
                  className="w-full sm:w-auto bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-4 rounded-full text-base font-medium transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 shadow-lg"
                  data-testid="reserve-cta"
                >
                  Quiero vivir REINICIA
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => scrollToSection('inversion')}
                className="w-full sm:w-auto border border-white/30 hover:bg-white/10 text-white px-8 py-4 rounded-full text-base font-medium transition-all inline-flex items-center justify-center gap-2 backdrop-blur-sm"
              >
                Ver inversión
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>

            {days != null && days > 0 && (
              <p className="text-xs text-white/60 mt-7 tracking-wider uppercase">
                Faltan <strong className="text-[#e8c75a]">{days} días</strong>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* PARA TI SI… — bloque de identificación */}
      <section id="para-ti" className="py-24 bg-gradient-to-b from-white to-[#FDFBF5]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Reconocerte</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))] leading-tight">
              Quizá este retiro<br />
              <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>es para ti si…</span>
            </h2>
          </div>
          <ul className="space-y-3.5">
            {[
              'Sientes que haces muchas cosas, pero algo dentro de ti sigue desordenado.',
              'Estás cansada/o de sostener, pensar, resolver o anticipar.',
              'Necesitas cerrar una etapa, pero no sabes exactamente cómo hacerlo.',
              'Sientes que tu energía está dispersa o demasiado mezclada con lo externo.',
              'Has hecho trabajo personal o espiritual, pero necesitas una experiencia más corporal, real y encarnada.',
              'Te apetece compartir unos días en naturaleza, con profundidad, pero sin exigencia.',
              'Necesitas parar antes de seguir avanzando.',
              'Sientes que tu vida te está pidiendo un cambio de posición interna.',
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-3 bg-white/70 border border-[#D4A84B]/20 rounded-2xl p-4 sm:p-5 text-gray-700 leading-relaxed" data-testid={`para-ti-${i}`}>
                <Sparkles className="w-4 h-4 text-[#D4A84B] mt-1 flex-shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
          <p className="text-center text-gray-500 italic mt-10 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
            No necesitas experiencia previa.<br />Solo apertura y permiso para volver a escucharte.
          </p>
        </div>
      </section>

      {/* QUE SIGNIFICA REINICIAR — bloque de transformación */}
      <section id="reiniciar" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Volver a ti</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">
              ¿Qué significa <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>reiniciar?</span>
            </h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5">
            <p>Reiniciar no es desaparecer, huir o borrar lo vivido.</p>
            <p className="text-xl sm:text-2xl font-heading text-[#8a7428] text-center py-3" style={{ fontFamily: 'Georgia, serif' }}>
              Reiniciar es detenerte lo suficiente como<br />
              para poder mirar tu vida desde otro lugar.
            </p>
            <p>
              Es soltar ruido, ordenar energía, recuperar presencia y volver a sentir qué parte de ti quiere empezar a vivir con más verdad.
            </p>
            <p>
              En REINICIA trabajaremos con prácticas energéticas, dinámicas grupales, naturaleza, meditación, cuerpo, presencia y herramientas de introspección para acompañar un proceso de cierre, limpieza interna y nueva orientación vital.
            </p>
          </div>

          <div className="mt-14 bg-gradient-to-br from-[#0e2144] to-[#1a3a5c] rounded-3xl p-8 sm:p-10 text-white text-center">
            <Wind className="w-7 h-7 text-[#D4A84B] mx-auto mb-4" />
            <p className="text-lg sm:text-xl italic leading-relaxed text-white/95" style={{ fontFamily: 'Georgia, serif' }}>
              «No se trata de forzar una transformación.<br />
              Se trata de crear el espacio adecuado para<br />
              que algo dentro de ti pueda recolocarse.»
            </p>
          </div>
        </div>
      </section>

      {/* CUANDO VUELVAS A CASA — promesa concreta */}
      <section id="cuando-vuelvas" className="py-24 bg-[#FDFBF5]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Lo que te llevas</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">
              Cuando vuelvas <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>a casa…</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-5 leading-relaxed">
              No te llevarás teoría. Te llevarás una nueva manera de estar contigo.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Podrás sentir más claridad sobre la etapa que estás atravesando.',
              'Habrás recuperado energía que estaba dispersa en preocupaciones, vínculos o procesos abiertos.',
              'Tendrás más presencia corporal y sensación de eje interno.',
              'Podrás reconocer qué necesitas soltar y qué quieres empezar a cuidar.',
              'Te llevarás prácticas sencillas para volver a ti cuando el ruido externo aumente.',
              'Habrás compartido un espacio humano, profundo y cuidado con otras personas en proceso.',
              'Podrás regresar con una sensación más ordenada de dirección interna.',
            ].map((it, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-2xl border border-[#D4A84B]/20 p-5" data-testid={`cuando-vuelvas-${i}`}>
                <div className="w-7 h-7 rounded-full bg-[#FDFBF5] border border-[#D4A84B]/40 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-[#8a7428]" />
                </div>
                <span className="text-gray-700 text-sm leading-relaxed">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LA EXPERIENCIA — qué viviremos */}
      <section id="experiencia" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">La experiencia</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">
              ¿Qué viviremos <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>durante estos días?</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-5 leading-relaxed">
              No será un retiro rígido ni lleno de actividades por cumplir. Habrá una estructura cuidada, pero también espacio para que el proceso del grupo vaya mostrando lo que necesita.
            </p>
          </div>
          <div className="bg-gradient-to-br from-[#FDFBF5] to-white rounded-3xl border border-[#D4A84B]/20 p-8 sm:p-10">
            <p className="text-sm uppercase tracking-wider text-[#8a7428] mb-6 text-center">Compartiremos</p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-gray-700 text-sm">
              {[
                'Meditaciones guiadas',
                'Prácticas energéticas',
                'Trabajo con cuerpo y presencia',
                'Dinámicas de liberación y orden interno',
                'Espacios de integración',
                'Actividades conscientes en el bosque',
                'Conversaciones profundas y humanas',
                'Momentos de descanso real',
                'Piscina para refrescarnos, descansar, jugar y disfrutar',
                'Observación del cielo nocturno (Starlight)',
                'Convivencia sencilla y cuidada',
                'Cartas canalizadas creadas para el proceso',
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Sprout className="w-4 h-4 text-[#D4A84B] mt-0.5 flex-shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* DIFERENCIADOR — no es solo descansar */}
          <div className="mt-16 border-l-2 border-[#D4A84B]/40 pl-6 sm:pl-8 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[#8a7428] mb-3">El núcleo</p>
            <h3 className="font-heading text-2xl sm:text-3xl text-[hsl(var(--text-primary))] mb-5 leading-tight">
              No es solo descansar.<br />
              <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>Es volver a tu eje.</span>
            </h3>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                El descanso es importante, pero REINICIA va más allá. La intención no es solo que desconectes unos días, sino que puedas reconectar con una parte más profunda, clara y ordenada de ti.
              </p>
              <p>
                Trabajaremos desde la presencia, el campo energético, el cuerpo, la cámara interna del corazón, el enraizamiento y herramientas iniciáticas que forman parte de mi metodología de acompañamiento.
              </p>
              <p className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>
                Busca ayudarte a volver a lo esencial.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CARTAS CANALIZADAS — herramienta nueva del retiro */}
      <section id="cartas" className="py-24 bg-gradient-to-b from-[#0e2144] via-[#13294b] to-[#0e2144] text-white relative overflow-hidden">
        {/* Decorative stars */}
        <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden="true">
          {[
            { t: '15%', l: '12%' }, { t: '22%', l: '78%' }, { t: '8%', l: '45%' },
            { t: '55%', l: '8%' }, { t: '65%', l: '92%' }, { t: '78%', l: '35%' },
            { t: '40%', l: '60%' }, { t: '85%', l: '70%' }, { t: '30%', l: '30%' },
          ].map((s, i) => (
            <span key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse" style={{ top: s.t, left: s.l, animationDelay: `${i * 0.3}s` }} />
          ))}
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#e8c75a] mb-4">Herramienta del retiro</p>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4A84B]/15 border border-[#D4A84B]/40 mb-6">
            <Sparkles className="w-7 h-7 text-[#e8c75a]" />
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl mb-6 leading-tight">
            Cartas canalizadas<br />
            <span className="italic text-[#e8c75a]" style={{ fontFamily: 'Georgia, serif' }}>para acompañar tu proceso</span>
          </h2>
          <div className="space-y-5 text-white/90 leading-relaxed text-base sm:text-lg max-w-2xl mx-auto">
            <p>
              Este año entregaré un <strong className="text-white">nuevo formato de cartas canalizadas</strong> creadas especialmente para el retiro.
            </p>
            <p className="text-white/75 italic" style={{ fontFamily: 'Georgia, serif' }}>
              No serán cartas para predecir nada.
            </p>
            <p>
              Serán una herramienta de introspección, claridad y acompañamiento interno. Las utilizaremos durante el proceso para abrir preguntas, reconocer movimientos internos, ordenar lo que esté emergiendo y ayudarte a escuchar con más profundidad qué necesita tu vida en este momento.
            </p>
            <p className="text-[#e8c75a] font-heading text-xl pt-2" style={{ fontFamily: 'Georgia, serif' }}>
              Cada carta es una puerta simbólica para mirar dentro de ti con más claridad.
            </p>
          </div>
        </div>
      </section>

      {/* EL LUGAR */}
      <section id="lugar" className="py-24 bg-[#FDFBF5]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">El lugar</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))] mb-4">
              {retreat.location_name}
            </h2>
            <p className="text-gray-600">{retreat.location_city} · {retreat.location_region}</p>
            <p className="text-gray-600 max-w-2xl mx-auto mt-5 leading-relaxed">
              Un espacio rodeado de naturaleza y silencio, ideal para permitir que el cuerpo desacelere y vuelva a escuchar. La energía del lugar, la tierra, los ritmos naturales y la convivencia consciente forman parte esencial de la experiencia.
            </p>
          </div>
          <div className="bg-gradient-to-br from-white to-[#FBF6E8] rounded-3xl border border-[#D4A84B]/20 p-8 sm:p-12">
            <div className="grid sm:grid-cols-3 gap-7 text-sm">
              <div>
                <Moon className="w-6 h-6 text-[#D4A84B] mb-2" />
                <p className="font-medium text-[hsl(var(--text-primary))]">Certificación Starlight</p>
                <p className="text-gray-600 mt-1">Observación astronómica en uno de los cielos más limpios de España.</p>
              </div>
              <div>
                <Sprout className="w-6 h-6 text-[#D4A84B] mb-2" />
                <p className="font-medium text-[hsl(var(--text-primary))]">Naturaleza viva</p>
                <p className="text-gray-600 mt-1">Olivos centenarios, paseos contemplativos y silencio fértil.</p>
              </div>
              <div>
                <Coffee className="w-6 h-6 text-[#D4A84B] mb-2" />
                <p className="font-medium text-[hsl(var(--text-primary))]">Cocina monástica</p>
                <p className="text-gray-600 mt-1">Casera, de proximidad. Adaptada a cada cuerpo.</p>
              </div>
            </div>
            <div className="mt-8 pt-7 border-t border-[#D4A84B]/15 text-center text-sm text-gray-600">
              <p className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4A84B]" /> Del 19 al 22 de julio de 2026</p>
            </div>
          </div>

          {/* Vídeo del monasterio */}
          <div className="mt-12" data-testid="monastery-video">
            <p className="text-center text-xs uppercase tracking-[0.25em] text-[#8a7428] mb-5">Mira el monasterio</p>
            <div className="relative aspect-video w-full max-w-3xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-[#D4A84B]/20 bg-black">
              <iframe
                src="https://www.youtube.com/embed/rRQZ_UUazwo?rel=0"
                title="Monasterio Santa María de El Olivar"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* GALERIA — fotos del monasterio y de la edición anterior */}
      <section id="galeria" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Galería</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">
              Memorias de El Olivar
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mt-5 leading-relaxed">
              El monasterio y la edición anterior del retiro (agosto 2025). Pulsa cualquier imagen para verla en grande.
            </p>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="retreat-gallery">
            {GALLERY_IMAGES.map((img, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setLightboxIdx(idx)}
                className="mb-4 block w-full break-inside-avoid overflow-hidden rounded-2xl group relative bg-[#0e2144]"
                data-testid={`gallery-thumb-${idx}`}
              >
                <img
                  src={img.src}
                  alt={img.caption}
                  loading="lazy"
                  className="block w-full h-auto group-hover:scale-[1.03] transition-transform duration-700"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-xs text-white font-medium tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>{img.caption}</p>
                </div>
              </button>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 italic mt-8 max-w-xl mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
            Cada retiro tiene su propia luz. Estas son algunas de las que ya pasaron.
          </p>
        </div>
      </section>

      {/* QUIZÁ NO ES PARA TI */}
      <section id="no-es-para-ti" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Honestidad</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">
              Este retiro quizá <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>no es para ti si…</span>
            </h2>
          </div>
          <ul className="space-y-3 max-w-2xl mx-auto">
            {[
              'Buscas un retiro turístico o solo unas vacaciones.',
              'Necesitas un programa cerrado al minuto.',
              'Quieres acumular más teoría sin entrar en experiencia.',
              'No estás disponible para mirar dentro de ti con honestidad.',
              'Buscas que alguien haga el proceso por ti.',
              'Prefieres una experiencia superficial sin profundidad emocional o energética.',
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4 text-gray-600" data-testid={`no-para-ti-${i}`}>
                <X className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>

          {/* Aviso para alumnas de edición anterior */}
          <div className="mt-12 bg-gradient-to-br from-[#FBF6E8] to-white border border-[#D4A84B]/30 rounded-2xl p-6 sm:p-7 max-w-2xl mx-auto" data-testid="prev-edition-notice">
            <p className="text-xs uppercase tracking-wider text-[#8a7428] mb-2">Para quien ya vino en agosto 2025</p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Esta edición será <strong>completamente distinta</strong> a la anterior. El espíritu y el lugar son los mismos, pero el contenido, las dinámicas y la intención son nuevas. Si ya viviste el retiro pasado, esta vez te sucederá otra cosa.
            </p>
          </div>
        </div>
      </section>

      {/* QUIEN SOY */}
      <section id="quien-soy" className="py-24 bg-[#FDFBF5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center order-2 lg:order-1">
              <div className="relative">
                <div className="w-72 h-80 lg:w-80 lg:h-96 rounded-2xl overflow-hidden shadow-xl">
                  <img src={HERMI_PHOTO} alt="Herminio" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#D4A84B]/20 rounded-full -z-10" />
              </div>
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B]">Quién soy</p>
              <div className="w-12 h-[2px] bg-[#D4A84B]" />
              <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))]">
                Soy Herminio
              </h2>
              <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed">
                Acompaño procesos de reconexión interna desde hace más de 15 años. No enseño a escapar de la vida, sino a habitarla con más presencia y menos esfuerzo.
              </p>
              <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                Mi trabajo no busca darte respuestas, sino ayudarte a encontrar tu propio sostén. En REINICIA pongo a tu disposición toda mi metodología de trabajo energético consciente, con la mayor delicadeza y la menor invasión posible.
              </p>
              <Link to="/quien-soy" className="inline-flex items-center gap-2 text-[#D4A84B] hover:text-[#b89220] font-medium">
                Conoce mi historia completa
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPORTE — coches compartidos */}
      <section id="transporte" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Cómo llegar</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">
              Llegamos <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>juntos</span>
            </h2>
          </div>
          <div className="bg-gradient-to-br from-[#FDFBF5] to-white rounded-3xl border border-[#D4A84B]/20 p-8 sm:p-10">
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                El transporte lo organizamos a nivel de <strong>coches compartidos</strong> entre quienes vamos al retiro. La mayoría salimos desde <strong>Barcelona</strong>.
              </p>
              <p>
                Cuando reserves tu plaza, dentro de tu espacio privado tendrás acceso a un <strong>tablón de coches compartidos</strong> donde podrás publicar que conduces, buscar plaza como acompañante o coordinarte con el grupo.
              </p>
              <p className="text-sm text-gray-600">
                Para otras combinaciones (otras ciudades, otros enlaces, otros horarios), escríbeme por WhatsApp y lo miramos juntos.
              </p>
            </div>
            <div className="mt-7 pt-7 border-t border-[#D4A84B]/20 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href="https://wa.me/34654827313?text=Hola%20Hermi%2C%20escribo%20por%20REINICIA%202026%20%E2%80%94%20me%20gustar%C3%ADa%20consultarte%20sobre%20transporte."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white px-6 py-3 rounded-full text-sm font-medium transition-colors flex-1"
                data-testid="transport-whatsapp-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                Consultar por WhatsApp
              </a>
              <a
                href="tel:+34654827313"
                className="inline-flex items-center justify-center gap-2 bg-white border border-[#D4A84B]/40 text-[#8a7428] hover:bg-[#FDFBF5] px-6 py-3 rounded-full text-sm font-medium transition-colors"
              >
                +34 654 827 313
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INVERSION + INCLUYE */}
      <section id="inversion" className="py-24 bg-gradient-to-b from-[#FDFBF5] to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Información práctica e inversión</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">
              Tu plaza en <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>REINICIA 2026</span>
            </h2>
          </div>

          {/* Practical info strip */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10 max-w-4xl mx-auto">
            {[
              { icon: Calendar, label: 'Fechas', value: '19 – 22 julio 2026' },
              { icon: MapPin, label: 'Lugar', value: `${retreat.location_name}, ${retreat.location_region || 'Teruel'}` },
              { icon: Moon, label: 'Duración', value: '4 días / 3 noches' },
              { icon: Star, label: 'Plazas', value: 'Limitadas' },
            ].map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="bg-white rounded-2xl border border-[#D4A84B]/20 p-4 flex items-start gap-3">
                  <Icon className="w-4 h-4 text-[#D4A84B] mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400">{it.label}</p>
                    <p className="text-sm font-medium text-[hsl(var(--text-primary))]">{it.value}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8">
            {/* Qué incluye */}
            <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Qué incluye</p>
              <h3 className="font-heading text-2xl text-[hsl(var(--text-primary))] mb-7">
                Todo cuidado para que tú solo te ocupes de ti
              </h3>
              <ul className="space-y-3 mb-6">
                {(retreat.includes || []).map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                    <Check className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              {retreat.not_included?.length > 0 && (
                <details className="text-sm text-gray-500 mt-6" data-testid="not-included-toggle">
                  <summary className="cursor-pointer hover:text-gray-700 select-none">Qué no incluye</summary>
                  <ul className="mt-3 space-y-2 ml-5 list-disc">
                    {retreat.not_included.map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                </details>
              )}
            </div>

            {/* PRICING CARD — precio único principal + desglose + individual a consultar */}
            <div className="bg-gradient-to-br from-[#FDFBF5] to-white rounded-3xl border-2 border-[#D4A84B]/40 p-8 shadow-lg shadow-[#D4A84B]/10" data-testid="pricing-card">
              {/* Principal */}
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-4">Tu plaza</p>
              <div className="mb-7">
                <p className="font-heading text-lg text-[hsl(var(--text-primary))] mb-1">Habitación compartida</p>
                <div className="flex items-baseline gap-2 mb-3">
                  <p className="font-heading text-5xl text-[#8a7428]" data-testid="price-double">
                    {retreat.pricing.double.amount.toFixed(2).replace('.', ',')} €
                  </p>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Incluye <strong>alojamiento en habitación compartida</strong>, pensión completa, sala, prácticas, acompañamiento grupal y experiencia completa del retiro.
                </p>
              </div>

              {/* Desglose siempre visible */}
              <div className="bg-white/80 rounded-2xl border border-[#D4A84B]/20 p-5 mb-6" data-testid="price-breakdown">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a7428] mb-3">Este importe incluye</p>
                <div className="space-y-2.5 text-sm">
                  <div className="flex items-baseline justify-between gap-3 pb-2.5 border-b border-gray-100">
                    <span className="text-gray-600">Alojamiento y pensión completa</span>
                    <span className="font-medium text-[hsl(var(--text-primary))] flex-shrink-0">252,50 €</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-gray-600">Acompañamiento, prácticas, sala<br className="hidden sm:block" /> y experiencia del retiro</span>
                    <span className="font-medium text-[hsl(var(--text-primary))] flex-shrink-0">200,00 €</span>
                  </div>
                </div>
              </div>

              {/* Individual = consultar */}
              <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6" data-testid="single-room-consult">
                <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Habitación individual</p>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm text-gray-600">Plazas muy limitadas · consultar disponibilidad y suplemento.</p>
                  <a
                    href="https://wa.me/34654827313?text=Hola%20Hermi%2C%20escribo%20por%20REINICIA%202026%20%E2%80%94%20me%20gustar%C3%ADa%20consultar%20disponibilidad%20de%20habitaci%C3%B3n%20individual."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#25D366] hover:text-[#1ebe57] flex-shrink-0"
                    data-testid="single-room-whatsapp"
                  >
                    Consultar por WhatsApp
                    <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </div>

              {/* Reserva info */}
              <div className="text-xs text-gray-600 mb-6 leading-relaxed">
                <p className="mb-1">
                  <strong>Reserva en lista gratuita</strong> — sin pago ahora.
                </p>
                <p>
                  La plaza se confirma con una <strong>paga y señal de {retreat.pricing.deposit_amount.toFixed(0)} €</strong>. El resto puede abonarse hasta el <strong>4 de julio</strong>.
                </p>
              </div>

              {ctaIsEnrolled ? (
                <Link to={`/mi-retiro/${slug}`} className="block w-full text-center bg-[#c9a227] hover:bg-[#b89220] text-white px-6 py-4 rounded-full text-sm font-medium tracking-wide transition-colors shadow-md">
                  Ya tienes plaza · ir a mi espacio
                </Link>
              ) : (
                <button onClick={() => setShowSignup(true)}
                        className="w-full bg-[#c9a227] hover:bg-[#b89220] text-white px-6 py-4 rounded-full text-sm font-medium tracking-wide transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 shadow-md"
                        data-testid="reserve-cta-pricing">
                  Reservar mi plaza <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-[#FDFBF5]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-3">Antes de venir</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[hsl(var(--text-primary))]">Preguntas frecuentes</h2>
          </div>
          <div className="space-y-3">
            {(retreat.faqs || []).map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden bg-white" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full text-left p-5 flex items-center justify-between hover:bg-[#FBF6E8] transition-colors"
                  >
                    <span className="font-medium text-[hsl(var(--text-primary))] pr-4">{f.q}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-[#8a7428]" /> : <ChevronDown className="w-4 h-4 text-[#8a7428]" />}
                  </button>
                  {isOpen && <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{f.a}</div>}
                </div>
              );
            })}
          </div>
          {retreat.cancellation_policy && (
            <div className="mt-8 bg-white border border-gray-100 rounded-2xl p-6 text-xs text-gray-500">
              <p className="uppercase tracking-wider text-gray-400 mb-2">Política de cancelación</p>
              <p className="whitespace-pre-line leading-relaxed">{retreat.cancellation_policy}</p>
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-gradient-to-b from-white via-[#FDFBF5] to-white pb-32 sm:pb-24">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <Heart className="w-7 h-7 text-[#D4A84B] mx-auto mb-5" fill="currentColor" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4A84B] mb-4">El momento</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[hsl(var(--text-primary))] mb-7 leading-tight">
            Quizá no necesitas<br />
            <span className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>
              seguir esperando.
            </span>
          </h2>
          <div className="text-gray-600 leading-relaxed space-y-4 max-w-xl mx-auto mb-9">
            <p>
              A veces una vida no cambia porque lo pienses más. Cambia cuando te das el espacio real para detenerte, escucharte, ordenar lo que llevas dentro y tomar una nueva posición interna.
            </p>
            <p className="text-[hsl(var(--text-primary))] font-medium">
              REINICIA es ese espacio.
            </p>
            <p className="italic text-[#8a7428]" style={{ fontFamily: 'Georgia, serif' }}>
              No vienes a desconectar de tu vida.<br />
              Vienes a volver a ella desde un lugar más verdadero.
            </p>
          </div>
          {ctaIsEnrolled ? (
            <Link to={`/mi-retiro/${slug}`} className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-10 py-4 rounded-full text-base font-medium tracking-wide transition-all hover:-translate-y-0.5 shadow-lg">
              Entrar a mi espacio <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <button onClick={() => setShowSignup(true)}
                    className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-10 py-4 rounded-full text-base font-medium tracking-wide transition-all hover:-translate-y-0.5 shadow-lg shadow-[#D4A84B]/30"
                    data-testid="reserve-cta-final">
              Quiero reservar mi plaza <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <p className="text-xs text-gray-400 mt-5">La reserva en lista es gratuita · sin pago ahora.</p>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightboxIdx != null && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          onClick={() => setLightboxIdx(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-5 right-5 p-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-[80]"
            data-testid="lightbox-close"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length); }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-[80]"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % GALLERY_IMAGES.length); }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-[80]"
            aria-label="Siguiente"
          >
            <ChevronDown className="w-5 h-5 -rotate-90" />
          </button>
          <div className="max-w-5xl w-full max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={GALLERY_IMAGES[lightboxIdx].src}
              alt={GALLERY_IMAGES[lightboxIdx].caption}
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
            <p className="text-white/80 text-sm mt-5 italic tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
              {GALLERY_IMAGES[lightboxIdx].caption}
            </p>
            <p className="text-white/40 text-xs mt-1">{lightboxIdx + 1} / {GALLERY_IMAGES.length}</p>
          </div>
        </div>
      )}

      {showSignup && (
        <SignupModal
          retreat={retreat}
          onClose={() => setShowSignup(false)}
          onSuccess={() => api.get(`/retreats/${slug}/me`).then(r => setMyEnrollment(r.data?.enrollment || null))}
        />
      )}
    </div>
  );
};

export default RetreatLanding;
